import { useId, type FC, type MouseEvent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { useDispatch } from 'react-redux';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { getFilterValues } from '../sbSharedRefineMenuHelpers.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { areFiltersApplied } from '../../helpers/appliedFilters/areFiltersApplied.ts';

// components
import { SbSharedRefineMenuInputCheckbox } from '../../SbSharedRefineMenu/SbSharedRefineMenuInput/SbSharedRefineMenuInputCheckbox.jsx';

// Constants
import { ON_SALE, SALE } from '../../SbSharedRefineMenu/sbSharedRefineMenuConstants.ts';

import styles from './SbSharedRefineMenuSaleInput.module.css';

const messages = defineMessages({
    title: {
        id: 'sb.SbMobileRefineMenuFilterSale.title',
        defaultMessage: 'On sale',
    },
});

import {
    type SbSharedRefineMenuSaleInput_itemSearch$key,
    type SbSharedRefineMenuSaleInput_itemSearch$data,
} from './__generated__/SbSharedRefineMenuSaleInput_itemSearch.graphql.ts';

type Filter = NonNullable<
    ArrayElement<NonNullable<SbSharedRefineMenuSaleInput_itemSearch$data['filters']>>
>;
type FilterValues = NonNullable<Filter['values']>;

type Props = {
    itemSearch: SbSharedRefineMenuSaleInput_itemSearch$key;
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuSaleInput: FC<Props> = ({
    itemSearch: itemSearchRef,
    trackingOptions,
}) => {
    const htmlId = useId();
    const intl = useIntl();
    const dispatch = useDispatch();

    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRefineMenuSaleInput_itemSearch on ItemSearchQueryConnection {
                ...areFiltersApplied_itemSearch
                filters {
                    name
                    values {
                        linkReference
                        linkable
                        displayName
                        urlLabel
                        count
                    }
                }
            }
        `,
        itemSearchRef
    );

    const filters = (itemSearch?.filters || []).filter(filterNulls);
    const onSaleFilterValues = getFilterValues(filters, ON_SALE) || [];
    const saleFilterValues: FilterValues =
        onSaleFilterValues.length > 0 ? onSaleFilterValues : getFilterValues(filters, SALE);

    const isSaleFilterApplied = areFiltersApplied(itemSearch, [SALE]);

    const checkboxes = saleFilterValues.map(filter => {
        const count = filter?.count;
        const urlLabel = filter?.urlLabel;
        return (
            <SbSharedRefineMenuInputCheckbox
                checkboxId={`${urlLabel}-checkbox-${htmlId}`}
                key={urlLabel}
                value={urlLabel || ''}
                linkReference={filter?.linkReference}
                linkable={filter?.linkable}
                checked={isSaleFilterApplied}
                onChange={(checked: boolean, event: MouseEvent<HTMLElement>) => {
                    dispatch(
                        updateUriRef({
                            filterName: SALE,
                            filterValue: filter,
                            isDeselected: !checked,
                            event,
                            trackingOptions,
                        })
                    );
                }}
                text={intl.formatMessage(messages.title)}
                className={styles.wrapper}
                count={count}
            />
        );
    });
    return <>{checkboxes}</>;
};
